import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './Header.module.css';
import { useAppContext } from '@/context/AppContext';
import ThemeToggle from '@/components/ThemeToggle/ThemeToggle';
import BackendSettingsModal from '@/components/BackendSettingsModal/BackendSettingsModal';

/**
 * Header component for the application
 * Provides navigation links and branding
 */
const Header: React.FC = () => {
  const { 
    // Common settings
    apiKey, 
    setApiKey, 
    
    // Source backend settings
    sourceBackend, 
    setSourceBackend, 
    sourcePort, 
    setSourcePort,
    isCustomSourceBackend,
    setIsCustomSourceBackend,
    getSourceBackendUrl,
    
    // Destination backend settings
    destBackend, 
    setDestBackend, 
    destPort, 
    setDestPort,
    isCustomDestBackend,
    setIsCustomDestBackend,
    getDestBackendUrl,
    
    // Source connection status
    connectionStatus,
    connectionError,
    connectionTimestamp,
    validateConnection,
    
    // Destination connection status
    destConnectionStatus,
    destConnectionError,
    destConnectionTimestamp,
    validateDestConnection
  } = useAppContext();
  
  // State to force re-render for timestamp updates
  const [, setForceUpdate] = useState(0);
  
  // Update the timestamp display every 5 seconds
  useEffect(() => {
    if (connectionStatus === 'connected') {
      const interval = setInterval(() => {
        setForceUpdate(prev => prev + 1);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [connectionStatus]);

  // Function to display masked API key
  const getDisplayApiKey = (key: string): string => {
    if (!key) return '';
    if (key.length <= 5) return key;
    
    // Show first 2 and last 3 characters, mask the rest with dots
    const firstPart = key.slice(0, 2);
    const lastPart = key.slice(-3);
    const middleLength = key.length - 5;
    const maskedMiddle = '•'.repeat(Math.min(middleLength, 8));
    
    return `${firstPart}${maskedMiddle}${lastPart}`;
  };
  
  // Keep the full API key in state but display the shortened version
  const [displayApiKey, setDisplayApiKey] = useState<string>(getDisplayApiKey(apiKey));

  // State for modals
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [destModalOpen, setDestModalOpen] = useState(false);
  
  // State for hover tooltips
  const [showSourceUrl, setShowSourceUrl] = useState(false);
  const [showDestUrl, setShowDestUrl] = useState(false);
  
  // Open source settings modal
  const openSourceModal = () => {
    setSourceModalOpen(true);
  };
  
  // Open destination settings modal
  const openDestModal = () => {
    setDestModalOpen(true);
  };
  
  // Close modals
  const closeSourceModal = () => {
    setSourceModalOpen(false);
    // Don't validate here - will be done in the modal if settings changed
  };
  
  const closeDestModal = () => {
    setDestModalOpen(false);
    // Don't validate here - will be done in the modal if settings changed
  };


  
  // Get status for source backend
  const getSourceStatus = () => {
    if (!apiKey) return undefined;
    
    switch (connectionStatus) {
      case 'connected':
        return {
          status: 'connected',
          title: 'Source Connected'
        };
      case 'connecting':
        return {
          status: 'connecting',
          title: 'Source Connecting...'
        };
      case 'failed':
        return {
          status: 'failed',
          title: `Source: ${connectionError || 'Connection failed'}`
        };
      default:
        return {
          status: 'unknown',
          title: 'Waiting for source connection validation'
        };
    }
  };
  
  // Get status for destination backend
  const getDestStatus = () => {
    if (!apiKey) return undefined;
    
    switch (destConnectionStatus) {
      case 'connected':
        return {
          status: 'connected',
          title: 'Destination Connected'
        };
      case 'connecting':
        return {
          status: 'connecting',
          title: 'Destination Connecting...'
        };
      case 'failed':
        return {
          status: 'failed',
          title: `Destination: ${destConnectionError || 'Connection failed'}`
        };
      default:
        return {
          status: 'unknown',
          title: 'Waiting for destination connection validation'
        };
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <Link to="/" className={styles.logoText}>
            CITYFLO TRACKING SIM
          </Link>
        </div>
        
        <div className={styles.configSection}>
          {/* API Key Input */}
          <div className={styles.apiKeyContainer}>
            <input
              type="text"
              id="apiKey"
              className={styles.apiKeyInput}
              value={displayApiKey}
              onFocus={() => setDisplayApiKey(apiKey)} // Show full key when focused
              onBlur={() => setDisplayApiKey(getDisplayApiKey(apiKey))} // Show shortened key when blurred
              onChange={(e) => {
                setApiKey(e.target.value);
                setDisplayApiKey(e.target.value);
              }}
              placeholder="API Key"
            />
          </div>
          
          {/* Backend Buttons */}
          <div className={styles.backendButtons}>
            <button 
              className={styles.backendButton} 
              onClick={openSourceModal}
              onMouseEnter={() => setShowSourceUrl(true)}
              onMouseLeave={() => setShowSourceUrl(false)}
              title={getSourceStatus()?.title || "Source Backend Settings"}
              data-status={getSourceStatus()?.status}
            >
              <span className={styles.backendButtonText}>
                Source
                {showSourceUrl && (
                  <span className={styles.backendUrlTooltip}>
                    {getSourceBackendUrl()}
                  </span>
                )}
              </span>
              <span className={styles.backendButtonIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </span>
            </button>
            
            <button 
              className={styles.backendButton} 
              onClick={openDestModal}
              onMouseEnter={() => setShowDestUrl(true)}
              onMouseLeave={() => setShowDestUrl(false)}
              title={getDestStatus()?.title || "Destination Backend Settings"}
              data-status={getDestStatus()?.status}
            >
              <span className={styles.backendButtonText}>
                Destination
                {showDestUrl && (
                  <span className={styles.backendUrlTooltip}>
                    {getDestBackendUrl()}
                  </span>
                )}
              </span>
              <span className={styles.backendButtonIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
              </span>
            </button>
          </div>
          
          {/* Modals */}
          <BackendSettingsModal 
            isOpen={sourceModalOpen} 
            onClose={closeSourceModal} 
            type="source" 
          />
          
          <BackendSettingsModal 
            isOpen={destModalOpen} 
            onClose={closeDestModal} 
            type="destination" 
          />
          
          <div className={styles.themeToggleContainer}>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
